'use client';

import { skipToken, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../contexts/ApiContext';
import { queryKeys } from '../../../lib/queryClient';
import type { AchievementStats } from '../../../services/generated/Api';

export type { AchievementStats, AchievementStats as AchievementStatsResponse };

export class AchievementStatsNotFoundError extends Error {
  constructor() {
    super('achievement stats not found');
    this.name = 'AchievementStatsNotFoundError';
  }
}

function thrownStatus(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const status: unknown = Reflect.get(err, 'status');
  return typeof status === 'number' ? status : undefined;
}

export function useAchievementStats(key?: string, options?: { enabled?: boolean }) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.achievementsStats(key),
    queryFn: key
      ? async (): Promise<AchievementStats> => {
          try {
            return await api.api.achievementsControllerGetStats(key);
          } catch (err) {
            if (thrownStatus(err) === 404) throw new AchievementStatsNotFoundError();
            throw err;
          }
        }
      : skipToken,
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
  });
}
